import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studentsAPI, programsAPI, locationsAPI, uploadAPI } from '../lib/api';
import { dispatchActivityRefresh } from '../lib/activityRefresh';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { PhotoUpload } from '../components/PhotoUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Search, Loader2, User, Filter, X, Camera, Upload, UserCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Clickable Photo Avatar Component for list items
const ClickablePhotoAvatar = ({ student, onPhotoUpdate, canEdit }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, 'student', student.id);
      toast.success('Photo updated successfully');
      dispatchActivityRefresh();
      if (onPhotoUpdate) {
        onPhotoUpdate(student.id, response.data.photo_url);
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleClick = (e) => {
    if (canEdit) {
      e.stopPropagation();
      fileInputRef.current?.click();
    }
  };

  const photoUrl = getPhotoUrl(student.photo_url);

  return (
    <div className="relative group">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
        className="hidden"
        data-testid={`student-photo-input-${student.id}`}
      />
      <div 
        className={`w-10 h-10 rounded-full overflow-hidden flex items-center justify-center ${canEdit ? 'cursor-pointer' : ''}`}
        onClick={handleClick}
        data-testid={`student-avatar-${student.id}`}
      >
        {uploading ? (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          </div>
        ) : photoUrl ? (
          <img 
            src={photoUrl} 
            alt={student.name} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
            {student.name.split(' ').map(n => n[0]).join('')}
          </div>
        )}
      </div>
      {canEdit && !uploading && (
        <div 
          className="absolute inset-0 w-10 h-10 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
          onClick={handleClick}
        >
          <Camera className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );
};

const Students = () => {
  const navigate = useNavigate();
  const { user, canEdit, isAdmin } = useAuth();
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProgram, setFilterProgram] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    student_type: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    sex: '',
    // Child-only fields
    grade: '',
    guardian_name: '',
    guardian_relationship: '',
    guardian_primary_phone: '',
    guardian_email: '',
    // Adult-only fields
    email: '',
    phone: '',
    // Common fields
    program_id: '',
    location_id: '',
    school_id: '',
    start_date: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [studentsRes, programsRes, locationsRes] = await Promise.all([
        studentsAPI.getAll({}),
        programsAPI.getAll(),
        locationsAPI.getAll({}),
      ]);
      setStudents(studentsRes.data);
      setPrograms(programsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpdate = (studentId, photoUrl) => {
    setStudents(prev => 
      prev.map(s => s.id === studentId ? { ...s, photo_url: photoUrl } : s)
    );
  };

  const resetForm = () => {
    setFormData({
      student_type: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      sex: '',
      grade: '',
      guardian_name: '',
      guardian_relationship: '',
      guardian_primary_phone: '',
      guardian_email: '',
      email: '',
      phone: '',
      program_id: '',
      location_id: '',
      school_id: '',
      start_date: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.student_type) {
      toast.error('Please select Student Type');
      return;
    }
    if (!formData.first_name || !formData.last_name) {
      toast.error('First Name and Last Name are required');
      return;
    }
    if (!formData.date_of_birth) {
      toast.error('Date of Birth is required');
      return;
    }
    if (!formData.program_id || !formData.location_id) {
      toast.error('Program and Location are required');
      return;
    }

    // Validate type-specific fields
    if (formData.student_type === 'child') {
      if (!formData.guardian_name) {
        toast.error('Guardian Name is required for child students');
        return;
      }
      if (!formData.guardian_primary_phone) {
        toast.error('Guardian Phone is required for child students');
        return;
      }
    } else if (formData.student_type === 'adult') {
      if (!formData.email) {
        toast.error('Email is required for adult students');
        return;
      }
      if (!formData.phone) {
        toast.error('Phone is required for adult students');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Build payload based on student type
      const payload = {
        student_type: formData.student_type,
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth,
        sex: formData.sex || null,
        program_id: formData.program_id,
        location_id: formData.location_id,
        school_id: formData.school_id || null,
        start_date: formData.start_date || null,
      };

      if (formData.student_type === 'child') {
        payload.grade = formData.grade || null;
        payload.guardian_name = formData.guardian_name;
        payload.guardian_relationship = formData.guardian_relationship || null;
        payload.guardian_primary_phone = formData.guardian_primary_phone;
        payload.guardian_email = formData.guardian_email || null;
      } else if (formData.student_type === 'adult') {
        payload.email = formData.email;
        payload.phone = formData.phone;
      }

      await studentsAPI.create(payload);
      toast.success('Student created successfully');
      dispatchActivityRefresh();
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : 'Failed to create student');
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStudents = students.filter((student) => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || student.status === filterStatus;
    const matchesProgram = filterProgram === 'all' || student.program_id === filterProgram;
    return matchesSearch && matchesStatus && matchesProgram;
  });

  // Check if user can add/edit/delete students (only admin/manager)
  const canManageStudents = () => {
    return user?.role === 'admin' || user?.role === 'manager';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Students</h1>
          <p className="text-slate-500">Manage student enrollment and progress</p>
        </div>
        <div className="flex gap-2">
          {canManageStudents() && (
            <>
              <Button variant="outline" onClick={() => navigate('/bulk-enrollment')} data-testid="bulk-enroll-btn">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Enroll
              </Button>
              <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Student
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-students"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterProgram} onValueChange={setFilterProgram}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-program">
                <SelectValue placeholder="Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead className="hidden sm:table-cell">Type</TableHead>
                <TableHead className="hidden md:table-cell">Program</TableHead>
                <TableHead className="hidden lg:table-cell">Location</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-400">No students found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStudents.map((student) => (
                  <TableRow
                    key={student.id}
                    className="table-row-interactive"
                    onClick={() => navigate(`/students/${student.id}`)}
                    data-testid={`student-row-${student.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <ClickablePhotoAvatar 
                          student={student} 
                          onPhotoUpdate={handlePhotoUpdate}
                          canEdit={canManageStudents()}
                        />
                        <div>
                          <p className="font-medium text-slate-900">{student.name}</p>
                          <p className="text-sm text-slate-500">
                            {student.age !== null ? `Age: ${student.age}` : 'Age: N/A'}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant={student.student_type === 'adult' ? 'secondary' : 'outline'} className="text-xs">
                        {student.student_type === 'adult' ? (
                          <><UserCircle className="w-3 h-3 mr-1" /> Adult</>
                        ) : (
                          <><Users className="w-3 h-3 mr-1" /> Child</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-slate-600">{student.program_name || '-'}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-slate-600">{student.location_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">
                            {Math.round(student.progress_percent)}%
                          </span>
                          <span className="text-xs text-slate-400">
                            {student.lessons_completed}/{student.lessons_total}
                          </span>
                        </div>
                        <Progress value={student.progress_percent} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={student.status === 'active' ? 'default' : 'secondary'}>
                        {student.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Student Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Student</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Student Type Selector - Required First */}
            <div className="space-y-2">
              <Label className="text-base font-semibold">Student Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, student_type: 'child' })}
                  className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                    formData.student_type === 'child' 
                      ? 'border-primary bg-primary/5 text-primary' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="student-type-child"
                >
                  <Users className="w-6 h-6" />
                  <span className="font-medium">Child</span>
                  <span className="text-xs text-slate-500">Requires guardian info</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, student_type: 'adult' })}
                  className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-all ${
                    formData.student_type === 'adult' 
                      ? 'border-primary bg-primary/5 text-primary' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid="student-type-adult"
                >
                  <UserCircle className="w-6 h-6" />
                  <span className="font-medium">Adult</span>
                  <span className="text-xs text-slate-500">Direct contact info</span>
                </button>
              </div>
            </div>

            {formData.student_type && (
              <>
                {/* Personal Information */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-700 mb-3">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        placeholder="First name"
                        data-testid="student-first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        placeholder="Last name"
                        data-testid="student-last-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_of_birth">Date of Birth *</Label>
                      <Input
                        id="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        data-testid="student-dob-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sex">Sex</Label>
                      <Select
                        value={formData.sex}
                        onValueChange={(value) => setFormData({ ...formData, sex: value })}
                      >
                        <SelectTrigger data-testid="student-sex-select">
                          <SelectValue placeholder="Select sex" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Child-only Fields */}
                {formData.student_type === 'child' && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-slate-700 mb-3">Guardian Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="grade">Grade/Class</Label>
                        <Input
                          id="grade"
                          value={formData.grade}
                          onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                          placeholder="e.g., Grade 3"
                          data-testid="student-grade-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="guardian_relationship">Relationship</Label>
                        <Select
                          value={formData.guardian_relationship}
                          onValueChange={(value) => setFormData({ ...formData, guardian_relationship: value })}
                        >
                          <SelectTrigger data-testid="guardian-relationship-select">
                            <SelectValue placeholder="Relationship" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mother">Mother</SelectItem>
                            <SelectItem value="Father">Father</SelectItem>
                            <SelectItem value="Guardian">Guardian</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="guardian_name">Guardian Name *</Label>
                        <Input
                          id="guardian_name"
                          value={formData.guardian_name}
                          onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                          placeholder="Guardian name"
                          data-testid="guardian-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="guardian_primary_phone">Guardian Phone *</Label>
                        <Input
                          id="guardian_primary_phone"
                          value={formData.guardian_primary_phone}
                          onChange={(e) => setFormData({ ...formData, guardian_primary_phone: e.target.value })}
                          placeholder="Phone number"
                          data-testid="guardian-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="guardian_email">Guardian Email</Label>
                        <Input
                          id="guardian_email"
                          type="email"
                          value={formData.guardian_email}
                          onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                          placeholder="Email address"
                          data-testid="guardian-email-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Adult-only Fields */}
                {formData.student_type === 'adult' && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-slate-700 mb-3">Contact Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Email address"
                          data-testid="student-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="Phone number"
                          data-testid="student-phone-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Program Information */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-700 mb-3">Program Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Program *</Label>
                      <Select
                        value={formData.program_id}
                        onValueChange={(value) => setFormData({ ...formData, program_id: value })}
                      >
                        <SelectTrigger data-testid="student-program-select">
                          <SelectValue placeholder="Select program" />
                        </SelectTrigger>
                        <SelectContent>
                          {programs.map((program) => (
                            <SelectItem key={program.id} value={program.id}>
                              {program.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Location *</Label>
                      <Select
                        value={formData.location_id}
                        onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                      >
                        <SelectTrigger data-testid="student-location-select">
                          <SelectValue placeholder="Select location" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        data-testid="student-start-date-input"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !formData.student_type} data-testid="submit-student-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Student
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Students;
